/**
 * @file useFooterSources.ts
 * @description Данные для подсчёта колонок подвала, которых нет в меню
 * (phase19.16, приёмка Р2а; SB113, SB115): категории автосписка и документ
 * «Условия возврата» — цель ссылки платформы для пометки дублей.
 *
 * Оба запроса необязательны: без права на каталог или документы схема
 * показывает «авто» без числа и не помечает дубль возврата — меню при этом
 * работает. Ошибки молча не теряются: вместо данных — `null`, подписи это
 * учитывают («авто» вместо «авто, N кат.»).
 *
 * Партия «Юридические документы» (SB142): тот же список документов и групп
 * нужен своим строкам «Ссылок» — пометки «не опубликован», «документ удалён»
 * и окно «+ Добавить ссылку на документ»; `reloadLegal` — перечитать после
 * создания группы. Документ «виден» — по `visibleOnSite` сервера.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { catalogCategories } from "../../lib/api-catalog";
import { loadLegalTargets, type LegalTarget } from "../../lib/api-legal-targets";
import { invalidateLegalTargets, subscribeLegalTargets } from "../../lib/site-view/legal-targets-store";
import { footerAutoCategories } from "../../lib/site-view/footer-categories";
import { FOOTER_LEGAL_DOC_SLUGS, type FooterLegalDocKey, type FooterLegalDocsState } from "../../lib/site-view/footer-link-where";

export interface FooterSources {
  /** Категории автосписка по порядку; null — не загружены. */
  autoCategories: string[] | null;
  /** id опубликованного документа `returns-notice` (неопубликованного нет в полосе); null — нет или не загружен. */
  returnsDocId: string | null;
  /** Состояние документов оферты, политики и возврата (SB121 «где задаётся»); null — не загружены. */
  legalDocs: FooterLegalDocsState;
  /** Все документы и группы магазина (свои строки «Ссылок»); null — не загружены. */
  legalTargets: LegalTarget[] | null;
  /** Перечитать документы (после создания группы из окна подвала). */
  reloadLegal: () => void;
}

const DOC_KEYS: readonly FooterLegalDocKey[] = ["offer", "privacy", "returns"];

/** Документы магазина → состояние документов ссылок платформы. */
function docStates(items: ReadonlyArray<Pick<LegalTarget, "slug" | "visibleOnSite">>): NonNullable<FooterLegalDocsState> {
  const out: NonNullable<FooterLegalDocsState> = {};
  for (const key of DOC_KEYS) {
    const doc = items.find((d) => d.slug === FOOTER_LEGAL_DOC_SLUGS[key]);
    if (doc) out[key] = doc.visibleOnSite ? "published" : "draft";
  }
  return out;
}

type SourcesState = Omit<FooterSources, "reloadLegal">;

export function useFooterSources(): FooterSources {
  const [state, setState] = useState<SourcesState>({ autoCategories: null, returnsDocId: null, legalDocs: null, legalTargets: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([catalogCategories.list(), loadLegalTargets()]).then(([cats, docs]) => {
      if (cancelled) return;
      const items = docs.status === "fulfilled" ? docs.value : null;
      setState({
        autoCategories: cats.status === "fulfilled" ? footerAutoCategories(cats.value) : null,
        returnsDocId: items?.find((d) => d.slug === "returns-notice" && d.visibleOnSite)?.id ?? null,
        legalDocs: items ? docStates(items) : null,
        legalTargets: items,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // Общий список документов сброшен (создана группа, «Повторить») — перечитать.
  useEffect(() => subscribeLegalTargets(() => setAttempt((a) => a + 1)), []);
  const reloadLegal = useCallback(() => invalidateLegalTargets(), []);
  return useMemo(() => ({ ...state, reloadLegal }), [state, reloadLegal]);
}
